// src/hooks/useElementWidth.ts

import { useState, useLayoutEffect, RefObject } from 'react';

// Define the hook with a generic type T that must be a type of HTMLElement
export function useElementWidth<T extends HTMLElement>(ref: RefObject<T>): number {
  const [width, setWidth] = useState<number>(0);

  useLayoutEffect(() => {
    // The ref.current might be null on the first render, so we check for it
    const element = ref.current;

    if (!element) {
      return;
    }

    // The ResizeObserver is the perfect tool for this job
    // It efficiently watches for changes to an element's size
    const resizeObserver = new ResizeObserver(entries => {
      // We only expect one entry, but loop for safety
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          // The contentBoxSize is an array with one item for logical dimensions
          const contentBox = entry.contentBoxSize[0];
          setWidth(contentBox.inlineSize); // inlineSize is the width in the writing-mode direction
        } else {
          // Fallback for older browser versions
          setWidth(entry.contentRect.width);
        }
      }
    });

    resizeObserver.observe(element);

    // Cleanup function to prevent memory leaks
    return () => {
      resizeObserver.unobserve(element);
    };

  }, [ref]); // The effect depends on the ref object

  return width;
}